import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  useDisclosure,
  Link,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Switch,
  Heading,
} from "@chakra-ui/react";
import { POLICY_TYPES } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import useScreenType from "react-screentype-hook";
import { IgniteChakraModal } from "../Modal/IgniteChakraModal";

const COOKIE_CONSENT_KEY = "cookie_consent";
const COOKIE_PREFERENCES_KEY = "cookie_preferences";

type CookiePreferences = {
  essential: boolean;
  analytics: boolean;
  preferences: boolean;
};

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: true,
    preferences: true,
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (savedPreferences) {
      setPreferences(JSON.parse(savedPreferences));
    }

    if (consent === null) {
      setShowBanner(true);
    }
  }, []);

  const savePreferences = (newPreferences: CookiePreferences) => {
    setPreferences(newPreferences);
    localStorage.setItem(
      COOKIE_PREFERENCES_KEY,
      JSON.stringify(newPreferences)
    );
    localStorage.setItem(COOKIE_CONSENT_KEY, "custom");
    setShowBanner(false);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      preferences: true,
    };
    savePreferences(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      preferences: false,
    };
    savePreferences(onlyEssential);
  };

  const handleOpenCookieSettings = () => {
    onOpen();
  };

  if (!showBanner) return null;

  return (
    <Box
      position="fixed"
      bottom="0"
      left="0"
      right="0"
      bg="white"
      boxShadow="0 -2px 10px rgba(0,0,0,0.1)"
      p={4}
      zIndex="banner"
    >
      <VStack spacing={4} maxW="1200px" mx="auto" align="start">
        <Text>
          We use cookies to enhance your experience on our platform. By clicking
          "Accept All", you agree to our use of cookies. You can manage your
          preferences in our{" "}
          <Link
            color="blue.500"
            onClick={() => {
              navigate(`?${POLICY_TYPES.COOKIE}`, { replace: true });
            }}
            textDecoration="underline"
            cursor="pointer"
          >
            Cookie Policy
          </Link>
          .
        </Text>
        <HStack spacing={4}>
          <Button colorScheme="primary" size="sm" onClick={handleAcceptAll}>
            Accept All
          </Button>
          <Button variant="outline" size="sm" onClick={handleRejectAll}>
            Reject All
          </Button>
          <Button variant="ghost" size="sm" onClick={handleOpenCookieSettings}>
            Cookie Settings
          </Button>
        </HStack>
      </VStack>

      <CookieSettingsModal
        isOpen={isOpen}
        onClose={onClose}
        preferences={preferences}
        onSave={savePreferences}
      />
    </Box>
  );
};

const CookieSettingsModal = ({
  isOpen,
  onClose,
  preferences,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  preferences: CookiePreferences;
  onSave: (prefs: CookiePreferences) => void;
}) => {
  const [localPrefs, setLocalPrefs] = useState<CookiePreferences>(preferences);
  const { isMobile } = useScreenType();

  useEffect(() => {
    setLocalPrefs(preferences);
  }, [preferences]);

  const handleSave = () => {
    onSave(localPrefs);
  };

  return (
    <IgniteChakraModal
      size={isMobile ? "full" : "xl"}
      onClose={onClose}
      isOpen={isOpen}
      scrollBehavior={"inside"}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Cookie Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            <Box>
              <Heading as="h3" size="md" mb={2}>
                Essential Cookies
              </Heading>
              <Text mb={4}>
                These cookies are necessary for the website to function and
                cannot be switched off. They are usually only set in response to
                actions made by you which amount to a request for services, such
                as setting your privacy preferences, logging in, or filling in
                forms.
              </Text>
              <Text fontWeight="medium">Always Active</Text>
            </Box>

            <Box>
              <Heading as="h3" size="md" mb={2}>
                Analytics Cookies
              </Heading>
              <Text mb={4}>
                These cookies allow us to count visits and traffic sources so we
                can measure and improve the performance of our site.
              </Text>
              <Switch
                colorScheme="blue"
                isChecked={localPrefs.analytics}
                onChange={(e) =>
                  setLocalPrefs({ ...localPrefs, analytics: e.target.checked })
                }
              >
                Analytics Cookies
              </Switch>
            </Box>

            <Box>
              <Heading as="h3" size="md" mb={2}>
                Preference Cookies
              </Heading>
              <Text mb={4}>
                These cookies enable the website to provide enhanced
                functionality and personalization.
              </Text>
              <Switch
                colorScheme="blue"
                isChecked={localPrefs.preferences}
                onChange={(e) =>
                  setLocalPrefs({
                    ...localPrefs,
                    preferences: e.target.checked,
                  })
                }
              >
                Preference Cookies
              </Switch>
            </Box>

            <Text>
              For more information, please see our{" "}
              <Link
                color="blue.500"
                onClick={() => {
                  onClose();
                  navigate(`?${POLICY_TYPES.COOKIE}`, { replace: true });
                }}
              >
                Cookie Policy
              </Link>
              .
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="primary" onClick={handleSave}>
            Save Preferences
          </Button>
        </ModalFooter>
      </ModalContent>
    </IgniteChakraModal>
  );
};
